<!--
  Complete Job Modal
  Used to finish/complete an active job. Records parts produced and end time.
  Opened from the view-event-modal or the production line card when completing a job.
-->
<app-oee-modal
    [title]="completeBatchLabel"
    [subtitle]="isSimplifiedComplete ? 'Are you done with this job?' : 'Record job completion details. Enter parts produced and end time.'"
    [contextName]="data.equipmentName"
    icon="check_circle"
    [useMaterialIcon]="true"
    iconColor="green"
    tint="green"
    [loading]="loadingCustomFields"
    loadingMessage="Loading custom fields...">

    <!-- Modal Content -->
    <ng-container modal-content>
        <!-- Job Selection (multi-job mode) -->
        @if (showBatchSelector) {
            <div class="mb-3">
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Select Job to Complete</mat-label>
                    <mat-select [value]="selectedBatch.batch_id" (selectionChange)="onBatchSelected($event.value)">
                        @for (b of allBatches; track b.batch_id) {
                            <mat-option [value]="b.batch_id">
                                {{ b.batch_number || 'Unnamed Job' }} — Started {{ b.start_ts | date:'short' }}
                            </mat-option>
                        }
                    </mat-select>
                    <mat-icon matPrefix>list</mat-icon>
                    <mat-hint>{{ allBatches.length }} jobs running — oldest selected by default</mat-hint>
                </mat-form-field>
            </div>
        }

        @if (isSimplifiedComplete) {
            <div class="simplified-summary">
                <span class="summary-item">
                    <mat-icon class="summary-icon">precision_manufacturing</mat-icon>
                    {{ data.equipmentName }}
                </span>
                @if (selectedBatch.product_name) {
                    <span class="summary-separator">·</span>
                    <span class="summary-item">
                        <mat-icon class="summary-icon">inventory_2</mat-icon>
                        {{ selectedBatch.product_name }}@if (selectedBatch.product_code) { ({{ selectedBatch.product_code }})}
                    </span>
                }
                @if (selectedBatch.work_order) {
                    <span class="summary-separator">·</span>
                    <span class="summary-item">
                        <mat-icon class="summary-icon">assignment</mat-icon>
                        {{ selectedBatch.work_order }}
                    </span>
                }
                <span class="summary-separator">·</span>
                <span class="summary-item elapsed" [class.invalid]="elapsedTime === 'Invalid (end before start)'">
                    <mat-icon class="summary-icon">timer</mat-icon>
                    {{ elapsedTime }}
                </span>
            </div>
        } @else {
            <div class="batch-info-card">
                <div class="info-row">
                    <mat-icon class="info-icon">precision_manufacturing</mat-icon>
                    <div class="info-content">
                        <span class="info-label">Equipment:</span>
                        <span class="info-value">{{ data.equipmentName }}</span>
                    </div>
                </div>

                @if (selectedBatch.product_name) {
                    <div class="info-row">
                        <mat-icon class="info-icon">inventory_2</mat-icon>
                        <div class="info-content">
                            <span class="info-label">Product:</span>
                            <span class="info-value">{{ selectedBatch.product_name }}@if (selectedBatch.product_code) { ({{ selectedBatch.product_code }})}</span>
                        </div>
                    </div>
                }

                @if (selectedBatch.work_order) {
                    <div class="info-row">
                        <mat-icon class="info-icon">assignment</mat-icon>
                        <div class="info-content">
                            <span class="info-label">Work Order:</span>
                            <span class="info-value">{{ selectedBatch.work_order }}</span>
                        </div>
                    </div>
                }

                <div class="info-row elapsed-time">
                    <mat-icon class="info-icon">timer</mat-icon>
                    <div class="info-content">
                        <span class="info-label">Elapsed Time:</span>
                        <span class="info-value highlight" [class.invalid]="elapsedTime === 'Invalid (end before start)'">{{ elapsedTime }}</span>
                    </div>
                </div>
            </div>
        }

        <form [formGroup]="completeBatchForm" class="complete-batch-form">

            @if (isSimplifiedComplete) {
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Job Name</mat-label>
                    <input matInput formControlName="batch_number" [matAutocomplete]="batchNameAuto"
                        placeholder="Enter job name">
                    <mat-autocomplete #batchNameAuto="matAutocomplete">
                        @for (item of filteredBatchNames | async; track item.batch_number) {
                            <mat-option [value]="item.batch_number">
                                <div class="batch-autocomplete-option">
                                    <span class="batch-name">{{ item.batch_number }}</span>
                                    @if (item.last_equipment_name) {
                                        <span class="batch-hint">
                                            <span class="hint-label">Last used:</span>
                                            <mat-icon class="hint-icon">precision_manufacturing</mat-icon>
                                            {{ item.last_equipment_name }}
                                            @if (item.last_used_ts) {
                                                <span class="last-used">• {{ formatLastUsed(item.last_used_ts) }}</span>
                                            }
                                        </span>
                                    }
                                </div>
                            </mat-option>
                        }
                    </mat-autocomplete>
                    <mat-icon matPrefix>qr_code_2</mat-icon>
                </mat-form-field>
                @if (!hasInvalidTimeRange) {
                    <button type="button" mat-stroked-button class="edit-times-toggle"
                        (click)="toggleEditTimes()" data-testid="btn-edit-times">
                        <mat-icon>schedule</mat-icon>
                        {{ editTimesExpanded ? 'Hide times' : 'Edit times' }}
                    </button>
                }
            }

            @if (showTimePickers) {
                <!-- Start Time -->
                <app-datetime-picker formControlName="start_ts"
                    label="Start Time"
                    hint="When the job started"
                    prefixIcon="schedule"
                    [initialDate]="startInitialDate">
                </app-datetime-picker>

                <!-- End Time -->
                <app-datetime-picker formControlName="end_ts"
                    label="End Time"
                    hint="When the job ended"
                    prefixIcon="schedule"
                    [initialDate]="today">
                </app-datetime-picker>
                @if (completeBatchForm.hasError('invalidTimeRange')) {
                    <mat-error class="time-range-error">
                        End time must be after start time
                    </mat-error>
                }
            }
            @if (!isSimplifiedComplete) {

                <!-- Job Name -->
                <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Job Name</mat-label>
                    <input matInput formControlName="batch_number" [matAutocomplete]="batchNameAutoFull"
                        placeholder="Enter job name">
                    <mat-autocomplete #batchNameAutoFull="matAutocomplete">
                        @for (item of filteredBatchNames | async; track item.batch_number) {
                            <mat-option [value]="item.batch_number">
                                <div class="batch-autocomplete-option">
                                    <span class="batch-name">{{ item.batch_number }}</span>
                                    @if (item.last_equipment_name) {
                                        <span class="batch-hint">
                                            <span class="hint-label">Last used:</span>
                                            <mat-icon class="hint-icon">precision_manufacturing</mat-icon>
                                            {{ item.last_equipment_name }}
                                            @if (item.last_used_ts) {
                                                <span class="last-used">• {{ formatLastUsed(item.last_used_ts) }}</span>
                                            }
                                        </span>
                                    }
                                </div>
                            </mat-option>
                        }
                    </mat-autocomplete>
                    <mat-icon matPrefix>qr_code_2</mat-icon>
                    <mat-hint>Enter or select a job name</mat-hint>
                </mat-form-field>

                <!-- Good Parts / Reject Parts -->
                <div class="parts-row">
                    <mat-form-field appearance="outline">
                        <mat-label>Good Parts</mat-label>
                        <input matInput type="number" formControlName="good_parts" min="0" data-testid="input-good-parts">
                        <mat-icon matPrefix class="success-icon">check_circle</mat-icon>
                        @if (completeBatchForm.get('good_parts')?.hasError('min')) {
                            <mat-error>
                                Must be 0 or greater
                            </mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>Reject Parts</mat-label>
                        <input matInput type="number" formControlName="reject_parts" min="0" data-testid="input-scrap-parts">
                        <mat-icon matPrefix class="error-icon">error</mat-icon>
                        @if (completeBatchForm.get('reject_parts')?.hasError('min')) {
                            <mat-error>
                                Must be 0 or greater
                            </mat-error>
                        }
                    </mat-form-field>
                </div>
                <div class="total-parts">Total Parts: {{ getTotalParts() }}</div>

                <!-- Custom Fields Section -->
                @if (customFields.length > 0 && !loadingCustomFields) {
                    <div class="custom-fields-section">
                        <div formGroupName="custom_field_values" class="custom-fields-grid">
                            @for (field of customFields; track field.id) {
                                <!-- Text Field -->
                                @if (field.type === 'text') {
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>{{ field.label }}{{ field.is_required ? ' *' : '' }}</mat-label>
                                        <input matInput [formControlName]="field.id" [placeholder]="field.default_value || ''">
                                        @if (completeBatchForm.get('custom_field_values')?.get(field.id)?.hasError('required')) {
                                            <mat-error>
                                                {{ field.label }} is required
                                            </mat-error>
                                        }
                                    </mat-form-field>
                                }
                                <!-- Number Field -->
                                @if (field.type === 'number') {
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>{{ field.label }}{{ field.is_required ? ' *' : '' }}</mat-label>
                                        <input matInput type="number" [formControlName]="field.id"
                                            [placeholder]="field.default_value || ''">
                                        @if (completeBatchForm.get('custom_field_values')?.get(field.id)?.hasError('required')) {
                                            <mat-error>
                                                {{ field.label }} is required
                                            </mat-error>
                                        }
                                    </mat-form-field>
                                }
                                <!-- Dropdown Field -->
                                @if (field.type === 'dropdown') {
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>{{ field.label }}{{ field.is_required ? ' *' : '' }}</mat-label>
                                        <mat-select [formControlName]="field.id">
                                            <mat-option value="">-- Select --</mat-option>
                                            @for (option of field.dropdown_options; track $index) {
                                                <mat-option [value]="option">
                                                    {{ option }}
                                                </mat-option>
                                            }
                                        </mat-select>
                                        @if (completeBatchForm.get('custom_field_values')?.get(field.id)?.hasError('required')) {
                                            <mat-error>
                                                {{ field.label }} is required
                                            </mat-error>
                                        }
                                    </mat-form-field>
                                }
                                <!-- Date Field -->
                                @if (field.type === 'date') {
                                    <app-datetime-picker [formControlName]="field.id"
                                        [label]="field.label + (field.is_required ? ' *' : '')" class="full-width">
                                    </app-datetime-picker>
                                }
                                <!-- Checkbox Field -->
                                @if (field.type === 'checkbox') {
                                    <div class="checkbox-field">
                                        <mat-checkbox [formControlName]="field.id">{{ field.label }}{{ field.is_required ? ' *'
                                            : '' }}</mat-checkbox>
                                        @if (completeBatchForm.get('custom_field_values')?.get(field.id)?.hasError('required')) {
                                            <mat-error class="checkbox-error">
                                                {{ field.label }} is required
                                            </mat-error>
                                        }
                                    </div>
                                }
                            }
                        </div>
                    </div>
                }
            }

        </form>

        <!-- Time Entries (hidden in simplified mode) -->
        @if (!isSimplifiedComplete && (data.allowsBatchOperators !== false || data.restrictBatchEquipment === true)) {
        <div class="time-entries-section mt-3">
            <h6 class="fw-semibold mb-2">Time Entries</h6>
            <app-batch-time-entries
                [batchId]="selectedBatch.batch_id"
                [equipmentId]="data.equipmentId"
                [siteId]="data.siteId"
                [allowOperators]="data.allowsBatchOperators !== false"
                [restrictBatchEquipment]="data.restrictBatchEquipment === true"
                mode="full"
                [readOnly]="false">
            </app-batch-time-entries>
        </div>
        }
    </ng-container>

    <!-- Modal Actions -->
    <ng-container modal-actions>
        <button mat-button (click)="onCancel()" [disabled]="submitting" data-testid="btn-cancel"
            [class.me-auto]="isSimplifiedComplete">
            Cancel
        </button>
        <button mat-stroked-button color="warn" (click)="openAbortModal()" [disabled]="submitting" data-testid="btn-abort">
            <mat-icon>cancel</mat-icon>
            Abort Instead
        </button>
        <span [matTooltip]="!canSubmit ? 'Complete all required fields to continue' : ''" matTooltipPosition="above">
            <button mat-raised-button color="primary" class="btn-success" (click)="onSubmit()"
                [disabled]="submitting || !canSubmit"
                data-testid="btn-confirm">
                <mat-icon>check_circle</mat-icon>
                {{ completeBatchLabel }}
            </button>
        </span>
    </ng-container>

</app-oee-modal>
